import { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { queryAPI } from '../services/api';
import { playTextToSpeechRealtime } from '../services/tts';

export const useChat = () => {
  const {
    token, 
    messages,
    setMessages,
    setLoading,
    setError,
    updateConversationTitle,
    activeConversationId
  } = useAppContext();

  const [copySuccess, setCopySuccess] = useState(false);
  const [showSourcesForMessage, setShowSourcesForMessage] = useState(null);
  const [playingMessageId, setPlayingMessageId] = useState(null);

  const handleReadAloud = async (text, messageIndex) => {
  if (playingMessageId !== null) return;

  try {
    setPlayingMessageId(messageIndex);
    
    const plainText = text
      .replace(/```[\s\S]*?```/g, '')
      .replace(/`{1,2}[^`\n]+`{1,2}/g, '')
      
      .replace(/^#{1,6}\s+/gm, '')
      
      .replace(/\*\*\*(.+?)\*\*\*/g, '$1')  
      .replace(/___(.+?)___/g, '$1')        
      .replace(/\*\*(.+?)\*\*/g, '$1')      
      .replace(/__(.+?)__/g, '$1')          
      .replace(/\*(.+?)\*/g, '$1')          
      .replace(/_(.+?)_/g, '$1')            
      
      .replace(/~~(.+?)~~/g, '$1')
      
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
      
      .replace(/\[([^\]]+)\]\[[^\]]*\]/g, '$1')
      
      .replace(/^[-*_]{3,}\s*$/gm, '')
      
      .replace(/^>\s+/gm, '')
      
      .replace(/^\s*[-*+]\s+/gm, '')
      .replace(/^\s*\d+\.\s+/gm, '')
      
      .replace(/<[^>]+>/g, '')
      
      .replace(/\|/g, ' ')
      .replace(/^[-:|\s]+$/gm, '')
      
      .replace(/\\([\\`*_{}[\]()#+\-.!])/g, '$1')
      
      .replace(/\n{3,}/g, '\n\n')           
      .replace(/[ \t]{2,}/g, ' ')           
      .replace(/^\s+|\s+$/gm, '')           
      .trim();
    
    await playTextToSpeechRealtime(plainText, 'fr');
    setPlayingMessageId(null);
  } catch (error) {
    console.error("Failed to play audio:", error);
    setError("Could not read the answer aloud.");
    setPlayingMessageId(null);
  }
};

  const handleQuerySubmit = async (query) => {
    if (!query.trim()) return;

    let cleanQuery = query.trim();
    if (cleanQuery.endsWith("?")) {
      cleanQuery = cleanQuery.slice(0, -1);
    }
    const userMessage = { role: 'user', content: cleanQuery };
    const botPlaceholder = { role: 'bot', content: '', sources: [] };
    updateConversationTitle(cleanQuery);
    setMessages(prev => [...prev, userMessage, botPlaceholder]);
    setError(null);
    setCopySuccess(false);
    setLoading(true);

    try {
      await queryAPI(cleanQuery, messages, token, activeConversationId, (eventType, data) => {
        setMessages(prev => {
          const lastMessage = prev[prev.length - 1];
          if (!lastMessage || lastMessage.role !== 'bot') return prev;
          let updatedLastMessage = { ...lastMessage };
          if (eventType === 'text_chunk' && data.content) {
            updatedLastMessage.content += data.content;
          } else if (eventType === 'sources') {
            updatedLastMessage.sources = data.sources || [];
          } else if (eventType === 'error') {
            setError(data.message);
          }
          return [...prev.slice(0, -1), updatedLastMessage];
        });
      });
    } catch (error) {
      setError('Failed to fetch from the server. Is the backend running?');
      console.error("Fetch error:", error);
      setMessages(prev => prev.filter(m => m !== userMessage && m !== botPlaceholder));
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
    document.body.removeChild(textArea);
  };

  return {
    handleQuerySubmit,
    copyToClipboard,
    copySuccess,
    showSourcesForMessage,
    setShowSourcesForMessage,
    playingMessageId,
    handleReadAloud,
  };
};